import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import reportWebVitals from './reportWebVitals';
import './styles/animations.css';
import { cssVariables } from './theme/designTokens';

// Inject the design-token CSS custom properties once, so components can migrate
// hardcoded hex to var(--color-*) incrementally and tenant branding flows through.
if (typeof document !== 'undefined') {
  const tokenStyle = document.createElement('style');
  tokenStyle.setAttribute('data-design-tokens', '');
  tokenStyle.textContent = cssVariables;
  document.head.appendChild(tokenStyle);
}

// Stale-tab recovery after a deploy. CRA names code-split chunks with
// a content hash; a new deploy renames every chunk. A tab that
// loaded the old index.html before the deploy will try to fetch
// chunk-OLD.js, get the SPA fallback HTML, and throw ChunkLoadError
// (the "Refused to execute script ... MIME type 'text/html'" trace).
//
// The fix is to reload the page once so the browser pulls the new
// index.html with the new chunk hashes. We guard with a sessionStorage
// flag so a genuine network failure doesn't put us in a reload loop.
const RELOAD_FLAG = 'chunk-reload-attempted';
function isChunkLoadError(err: any): boolean {
  if (!err) return false;
  const msg = String(err.message || err.reason?.message || '');
  return /ChunkLoadError|Loading chunk \d+ failed|Failed to fetch dynamically imported module/i.test(msg);
}
function handleChunkError(err: any) {
  if (!isChunkLoadError(err)) return;
  try {
    if (sessionStorage.getItem(RELOAD_FLAG) === '1') return; // already reloaded once
    sessionStorage.setItem(RELOAD_FLAG, '1');
  } catch { /* private mode — proceed anyway */ }
  // Force a fresh fetch of index.html.
  window.location.reload();
}
window.addEventListener('error', (e) => handleChunkError(e.error || e));
window.addEventListener('unhandledrejection', (e) => handleChunkError(e.reason));
// Clear the flag once the new bundle finishes booting cleanly.
window.addEventListener('load', () => {
  try { sessionStorage.removeItem(RELOAD_FLAG); } catch { /* ignore */ }
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
